/**
 * Permissions slice
 */

import { createAction, createSlice } from '@reduxjs/toolkit';

export interface Permission {
  _id?: string;
  id?: string;
  name: string;
  displayName: string;
  description?: string;
  resource: string;
  action: 'create' | 'read' | 'update' | 'delete' | 'manage' | 'assign';
  isActive: boolean;
  isSystemPermission?: boolean;
  createdAt?: number;
  updatedAt?: number;
}

const initialState = {
  permissions: [] as Permission[],
  currentPermission: null as Permission | null,
  loading: false,
  error: null as string | null,
};

const permissionsSlice = createSlice({
  name: 'permissions',
  initialState,
  reducers: {
    setPermissions(state, action) {
      state.permissions = action.payload;
      state.loading = false;
      state.error = null;
    },
    setCurrentPermission(state, action) {
      state.currentPermission = action.payload;
      state.loading = false;
      state.error = null;
    },
    addPermission(state, action) {
      state.permissions.push(action.payload);
      state.loading = false;
      state.error = null;
    },
    updatePermission(state, action) {
      const index = state.permissions.findIndex(
        (p) => (p._id || p.id) === (action.payload._id || action.payload.id)
      );
      if (index !== -1) {
        state.permissions[index] = action.payload;
      }
      if (state.currentPermission && (state.currentPermission._id || state.currentPermission.id) === (action.payload._id || action.payload.id)) {
        state.currentPermission = action.payload;
      }
      state.loading = false;
      state.error = null;
    },
    removePermission(state, action) {
      state.permissions = state.permissions.filter(
        (p) => (p._id || p.id) !== action.payload
      );
      state.loading = false;
      state.error = null;
    },
    setLoading(state, action) {
      state.loading = action.payload;
    },
    setError(state, action) {
      state.error = action.payload;
      state.loading = false;
    },
    clearError(state) {
      state.error = null;
    },
  },
});

// Reducer
export const permissionsReducer = permissionsSlice.reducer;

// Actions
export const {
  setPermissions,
  setCurrentPermission,
  addPermission,
  updatePermission,
  removePermission,
  setLoading,
  setError,
  clearError,
} = permissionsSlice.actions;

// Async Actions
export const fetchPermissions = createAction<{ resource?: string; action?: string; isActive?: boolean } | void>('PERMISSIONS/FETCH_ALL');
export const fetchPermission = createAction<string>('PERMISSIONS/FETCH_ONE');
export const createPermission = createAction<Omit<Permission, '_id' | 'id'>>('PERMISSIONS/CREATE');
export const updatePermissionAction = createAction<{ id: string; data: Partial<Permission> }>('PERMISSIONS/UPDATE');
export const deletePermission = createAction<string>('PERMISSIONS/DELETE');
export const fetchPermissionsByResource = createAction<string>('PERMISSIONS/FETCH_BY_RESOURCE');

